import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApiService } from './base-api-service';
import { BusyService } from './busy-service';
import { LoadState } from './delivery.service';

// Shared pagination types
export interface PaginationParams {
  page?: number;
  page_size?: number;
}

export interface PaginationInfo {
  page: number;
  page_size: number;
  total: number;
  pages: number;
}

// Request payloads

export interface LoadSaleOrder {
  id: number;
  sequence?: number;
}

export interface CreateLoadPayload {
  sale_order_ids: LoadSaleOrder[];
  note?: string;
}

export interface UpdateLoadPayload {
  load_id: number;
  write_date: string;
  sale_order_ids: LoadSaleOrder[];
  note?: string;
}

export interface DeleteLoadPayload {
  load_id: number;
  write_date: string;
}

export interface AssignDeliveryPayload {
  delivery_boy_id: number;
  load_ids: number[];
}

export interface FilterSaleOrdersPayload {
  has_load: boolean;
  startDate?: string;
  endDate?: string;
}

export interface FilterLoadRequest {
  delivery_boy_id?: number;
  startDate?: string;
  endDate?: string;
  page?: number;
  page_size?: number;
}

export interface GetLoadRequest {
  load_id: number;
}

export interface ResetLoadToDraftRequest {
  load_id: number;
  /** optional optimistic concurrency */
  write_date?: string;
}

export interface ResetLoadToDraftResponse {
  status: 'success' | 'error';
  message?: string;
  load_id?: number;
  state?: 'draft' | 'loaded' | 'in_transit' | 'ready_settlement' | 'settled';
  code?: string;
}

// Response models

export interface CreateLoadResponse {
  status: 'success' | 'error';
  load_id?: number;
  name?: string;
  message?: string;
}

export interface GenericResponse {
  status: 'success' | 'error';
  message?: string;
}

export interface FilteredSaleOrder {
  id: number;
  name: string;
  customerId: number;
  customerName: string;
  salesmanName: string;
  numberOfItems: number;
  totalAmount: number;
  dateOrder: string | null;
  sequence: number | 0;
  selected: boolean | null;
}

export interface FilterSaleOrdersResponse {
  status: 'success' | 'error';
  orders?: FilteredSaleOrder[];
  message?: string;
}

// Load-specific types

export interface FilteredLoad {
  id: number;
  name: string;
  deliveryBoyId?: number;
  deliveryBoyName?: string;
  salesManId?: number;
  salesManName?: string;
  createdAt: string;
  numberOfOrders: number;
  state: LoadState;
  selected: boolean | null;
  note?: string;
}

export interface FilterLoadsResponse {
  status: 'success' | 'error';
  message?: string;
  page?: number;
  page_size?: number;
  total?: number;
  pages?: number;
  loads?: FilteredLoad[];
  pagination?: PaginationInfo;
}

export interface LoadDetail {
  id: number;
  name: string;
  deliveryBoyId?: number;
  deliveryBoyName?: string;
  salesManId?: number;
  salesManName?: string;
  state: LoadState;
  write_date: string;
  createdAt: string;
  saleOrders: FilteredSaleOrder[];
  note?: string;
}

export interface GetLoadResponse {
  status: 'success' | 'error';
  message?: string;
  load?: LoadDetail;
}

// Delivery boy user interface and response

export interface DeliveryBoy {
  id: number;
  name: string;
}

export interface DeliveryBoysResponse {
  status: 'success' | 'error';
  users?: DeliveryBoy[];
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LoadManagementService extends BaseApiService {
  constructor(http: HttpClient, busy: BusyService) {
    super(http, busy);
  }

  /**
   * Create a new load
   */
  createLoad(payload: CreateLoadPayload): Observable<CreateLoadResponse> {
    return this.post<CreateLoadResponse>('load/create', payload);
  }

  /**
   * Update an existing load
   */
  updateLoad(payload: UpdateLoadPayload): Observable<GenericResponse> {
    return this.post<GenericResponse>('load/update', payload);
  }

  /**
   * Assign a delivery person to a load
   */
  assignDelivery(payload: AssignDeliveryPayload): Observable<GenericResponse> {
    return this.post<GenericResponse>('load/assign', payload);
  }

  /**
   * Filter sale orders
   */
  filterSaleOrders(payload: FilterSaleOrdersPayload): Observable<FilterSaleOrdersResponse> {
    return this.post<FilterSaleOrdersResponse>('so_loads/filter', payload);
  }

  /**
   * Filter loads with pagination and optional filters
   */
  filterLoads(payload: FilterLoadRequest): Observable<FilterLoadsResponse> {
    return this.post<FilterLoadsResponse>('loads/filter', payload);
  }

  /**
   * Get full details of a load by ID
   */
  getLoadById(payload: GetLoadRequest): Observable<GetLoadResponse> {
    return this.post<GetLoadResponse>('load/get', payload);
  }

  /**
  * Delete a load by ID and write_date
  */
  deleteLoad(payload: DeleteLoadPayload): Observable<GenericResponse> {
    return this.post<GenericResponse>('load/delete', payload);
  }

  /**
   * Get list of delivery boys (users in delivery boy group)
   */
  getDeliveryBoys(): Observable<DeliveryBoysResponse> {
    return this.get<DeliveryBoysResponse>('delivery_boys');
  }
}
